import type { PublicUser } from "@codvero/shared";
import { useMutation } from "@tanstack/react-query";
import { Camera, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "@/api/auth.ts";
import { useSetAuthUser } from "@/features/auth/session.ts";
import { cropToDataUrl } from "@/ui/AgentAvatar.tsx";
import { Alert, Button, Card, Field, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { UserAvatar } from "@/ui/UserAvatar.tsx";

/** The user's own name and photo, shown beside their messages in conversations. */
export function ProfileSettings({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const setUser = useSetAuthUser();
  const file = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(user.name);
  const save = useMutation({ mutationFn: authApi.updateProfile, onSuccess: (r) => setUser(r.user) });
  const pick = async (f: File | undefined) => {
    if (!f) return;
    try { save.mutate({ avatar: await cropToDataUrl(f, 160) }); } catch { /* not an image the browser can read */ }
  };
  return (
    <Card>
      <div className="space-y-4">
        {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
        <div className="flex items-center gap-4">
          <UserAvatar name={user.name} image={user.avatar} size="lg" />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" loading={save.isPending && save.variables?.avatar !== undefined && save.variables.avatar !== null} onClick={() => file.current?.click()}><Camera className="size-4" /> {user.avatar ? t("settings.profile.changePhoto") : t("settings.profile.addPhoto")}</Button>
            {user.avatar && <Button type="button" variant="ghost" onClick={() => save.mutate({ avatar: null })}><Trash2 className="size-4" /> {t("settings.profile.removePhoto")}</Button>}
          </div>
          <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim() && name.trim() !== user.name) save.mutate({ name }); }}>
          <div className="min-w-0 flex-1">
            <Field label={t("settings.profile.name")} hint={t("settings.profile.nameHint")}>
              <Input dir="auto" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <Button type="submit" disabled={!name.trim() || name.trim() === user.name} loading={save.isPending && save.variables?.name !== undefined}>{t("common.save")}</Button>
        </form>
      </div>
    </Card>
  );
}
